import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Loader from '../Layout/Loader'
import mainAxios from '../../services/mainAxios'
import { useAppContext, usePersistedState } from '../../AppContext'
import useStateWithCallback from '../../services/useStateWithCallback'
import { customStyles } from "../../design/selectStyle";
import { useTranslation } from "react-i18next";
import PrimeTable from '../PrimeTable/PrimeTable'
import DateRange from '../global/DateRange'
import ExportCsvPrime from '../global/ExportCsvPrime'
import { defaultTableRows } from '../../services/data'
import ReactSelectPagination from '../global/ReactSelectPagination'
import { bankStatusStyle, generateStatusStyle } from '../OpenBanking/OpenBankingUtils'
import { buildBankStatusOptions, buildProfiscStatusOptions } from '../OpenBanking/Payroll/PayrollGrid/usePayrollStatistics'
import MessageDisplay from '../global/MessageDisplay'

const moment = require("moment")

const OpenBankingLogs = () => {
    const { selectedCompany: [selectedCompany,],user:[user],company:[fullCompany] } = useAppContext()
    const [expandedRows, setExpandedRows] = React.useState([])
    const { t, i18n } = useTranslation('translations');
    const [logsForCSV, setLogsForCSV] = useState([]);

    const defaultFilters = {
        fromDate: new Date(),
        toDate: new Date(),
        params: {},
    };
    const [filters, setFilters] = React.useState(defaultFilters);

    const [companyFilterParams, setCompanyFilterParams] = useState({
        companyId: selectedCompany ?? null,
        isLoadingCompanies:false,
        companies:[]
    })

    const [companiesPagination,setCompaniesPagination]=useState({
        totalRows: 0,
        pageSize: 10,
        pageNumber: 1,
      })

    const [persistedRowPerPage, setPersistedRowPerPage,] = usePersistedState(
        10,
        "paymentLogsRowsPerPage"
    );

    const defaultSortCriteria={
        field: "recordDate",
        direction: "DESC"
    }

    const [tableRows, setTableRows] = useState({
        ...defaultTableRows,
        rowsPerPage:persistedRowPerPage,
        sortCriteria:defaultSortCriteria

    });
    const [searchQuery, setSearchQuery] = useState("");

    const [state, setState] = useStateWithCallback({
        logs: null,
        loading: false
    })
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        getFilteredResults({})
    }, [companyFilterParams.companyId])

    const getFilteredResults = ({
        pageSize = persistedRowPerPage ?? defaultTableRows.rowsPerPage,
        page = defaultTableRows.currentPage,
        sortCriteria = [ defaultSortCriteria ],
        getLogsForCsv = false,
        updatedFilters = filters,
        fromDate = defaultFilters.fromDate,
        toDate = defaultFilters.toDate
    } = {}) => {

        setLoading(true);
        if (logsForCSV.length > 0) setLogsForCSV([]);
        return mainAxios.post("/payment-logs/search", {
            object: "GetPaymentLogs",
            fromDate,
            toDate,
            params: updatedFilters.params,
            pagination: getLogsForCsv ? null : {
                pageSize,
                pageNumber: page,
            },
            sortCriteria,
            company:companyFilterParams?.companyId?.toString()
        }).then((res) => {
            setLoading(false);
            if (res.data.status) {
                if (getLogsForCsv) {
                    setLogsForCSV(res.data.content);
                } else {
                    setTableRows((tableRows) => ({
                        ...tableRows,
                        totalRows: res.data.totalSize,
                    }));
                    setState((state) => ({ ...state, logs: res.data.content }));
                }
                return true;
            }else{
                setTableRows(defaultTableRows);
                setState((state) => ({ ...state, logs:[]}));
            }

            return false;
        }).catch(error => {
            setLoading(false);
            return false;
        });
    };

    const types = [
        { value: "ERROR", label: "Error" },
        { value: "SUCCESS", label: "Success" },
    ]

    const getCompaniesResults = ({pageSize=10, pageNumber = 1,searchQuery,loadMore}={}) => {
    let requestBody = {
        object: "GetSimpleCompany",
        value: null,
        companyFilterParams: {
            companyLiveSearch:searchQuery,
        },
        pagination:{
            pageSize,
            pageNumber,
        },
        };
    return mainAxios.post('apiEndpoint/search',
    requestBody
    ).then(res => {
        if(res?.data?.status === true) {
            let companies = res.data.content
            setCompaniesPagination({
                pageNumber,
                pageSize,
                totalRows: res?.data?.totalSize,
            })
            setCompanyFilterParams((companyFilterParams)=>({...companyFilterParams,companies:loadMore?(companyFilterParams.companies ?? []).concat(companies):companies}))
        return companies
        }
        return []
    })
    .catch(err=>{
        return []
    })
    }

    const handleCompanyChange=(value)=>{
      setCompanyFilterParams(companyFilterParams=>({
        ...companyFilterParams,
        companyId:value?.id ?? selectedCompany,
        searchQuery:null,
      }))
      if(value===null){
        getCompaniesResults({pageNumber:1,pageSize:10,searchQuery:""})
      }
    }

    useEffect(()=>{
        getCompaniesResults({pageNumber:1,pageSize:10})
    },[])

    const companiesOptions=companyFilterParams.companies ?? []
    const headerButtonsLeft= user?.role?.id===1 && (
        <div style={{width:250}}>
        <ReactSelectPagination
            styles={customStyles}
            value={companiesOptions.find(company => company.id == companyFilterParams?.companyId )}
            options={companiesOptions}
            onChange={handleCompanyChange}
            placeholder={fullCompany?.name}
            getOptionLabel={(option)=>option.name}
            getOptionValue={(option)=>option.id}
            getFilteredResults={getCompaniesResults}
            pagination={companiesPagination}
            setPagination={setCompaniesPagination}
            isClearable={companyFilterParams?.companyId==selectedCompany?false: true}
        />
        </div>
    )

    
    const getStatus = useCallback((payroll)=>generateStatusStyle(payroll?.profiscStatus, t),[t]);
    const getBankStatus = useCallback((payroll)=>bankStatusStyle(payroll?.bankStatus, t),[t]);
    
    const bankStatusOptions = useMemo(()=>buildBankStatusOptions(null,t),[t]);
    const profiscStatusOptions = useMemo(() => buildProfiscStatusOptions(null, t), [t]);
    

    const columns = [
        {
            header: t(['openBankingLogs.action']),
            field: 'action',
            default:true,
            filter:true,
            body: log => log.action ? log.action : "",
            style: { minWidth: "200px", maxWidth: "200px" },
            mobile: { priority: 1, isTitle: true, group: "identity" },
        },
        {
            header: t(['openBankingLogs.message']),
            field: 'message',
            default:true,
            filter:true,
            mobile: { priority: 1, group: "details" },
            body: transaction =>  <MessageDisplay width={280} value={transaction.message ?? ""} />,
            style: { minWidth: "300px", maxWidth: "400px" },
        },
        {
            header: t(['subscription.type']),
            field: 'type',
            default:true,
            filter:true,
            mobile: { priority: 1, isBadge: true, group: "status" },
            filterType: "DROPDOWN",
            filterConfig: {
                options: types,
            },
            style: { minWidth: "170px", maxWidth: "170px" },
        },
        
        {
            header: t(['openBankingLogs.source']),
            field: 'source',
            default:true,
            filter:true,
            body: log => log.source ? log.source : "",
            style: { minWidth: "250px", maxWidth: "300px" },
            mobile: { priority: 1, group: "details" },
        },
        {
            header: t(['openBankingLogs.recordUser']),
            field: 'recordUser',
            default:true,
            filter:true,
            body: log => log.recordUser ? log.recordUser : "",
            style: { minWidth: "140px", maxWidth: "140px" },
            mobile: { priority: 1, group: "identity" },
        },
        {
            header: t(['openBankingLogs.recordDate']),
            field: 'recordDate',
            default:true,
            mobile: { priority: 1, group: "dates" },
            formattedExportValue: true,
            preventExcelFormat: true,
            filterType: "DATE",
            body: log => log.recordDate ? moment(log.recordDate).format("DD/MM/YYYY HH:mm") : "",
            style: { minWidth: "190px", maxWidth: "190px" },
            filter: true,
            sortable: false
        },
        {
            header: t(['openBankingLogs.endToEndId']),
            field: 'endToEndId',
            default:true,
            filter:true,
            body: log => log.endToEndId ? log.endToEndId : "",
            style: { minWidth: "300px", maxWidth: "350px" },
            mobile: { priority: 1, group: "reference" },
        },
        {
            header: t(['openBankingLogs.referenceNo']),
            field: 'referenceNo',
            default: false,
            filter:true,
            body: log => log.referenceNo ? log.referenceNo : "",
            style: { minWidth: "150px", maxWidth: "150px" },
        },
        {
            header: t(['openBanking.profiscStatus']),
            field: 'profiscStatus',
            default:true,
            filter:true,
            mobile: { priority: 1, isBadge: true, group: "status" },
            body: log => log.profiscStatus ? log.profiscStatus : "",
            style: { minWidth: "160px", maxWidth: "160px" },
            format:(order)=>getStatus(order).status,
            body: (order) => {
                const status = getStatus(order);
                return (
                    <h4 className="mb-0">
                        <small className={`badge badge-${status.badgeClass} ml-2`}>
                            {status?.status}
                        </small>
                    </h4>
                );
            },
            filterType: "DROPDOWN",
            filterConfig: {
                options: profiscStatusOptions,
            },
        },
        {
            header: t(['openBankingLogs.issueDateTime']),
            field: 'issueDateTime',
            default: false,
            formattedExportValue: true,
            preventExcelFormat: true,
            filterType: "DATE",
            body: log => log.issueDateTime ? moment(log.issueDateTime).format("DD/MM/YYYY HH:mm") : "",
            style: { minWidth: "190px", maxWidth: "190px" },
            filter: true,
        },
        {
            header: t(['openBankingLogs.originalBankStatus']),
            field: 'originalBankStatus',
            default: false,
            filter:true,
            body: log => log.originalBankStatus ? log.originalBankStatus : "",
            style: { minWidth: "180px", maxWidth: "180px" },
        },
        {
            header: t(['openBankingLogs.bankStatus']),
            field: 'bankStatus',
            default: false,
            filter:true,
            format:(payroll)=>getBankStatus(payroll).status,
            body: (payroll) => {
                const bankStatus =getBankStatus(payroll,t)

                return (
                    <h4 className="mb-0">
                        <small className={`badge badge-${bankStatus.badgeClass} ml-2`}>
                            {bankStatus?.status}
                        </small>                    
                    </h4>
                );
            },
            filterType: "DROPDOWN",
            filterConfig: {
                options: bankStatusOptions,
            },
            style: { minWidth: "150px", maxWidth: "150px" },
        },
        {
            header: t(['openBankingLogs.approvalUser']),
            field: 'approvalUser',
            default: false,
            filter:true,
            body: log => log.approvalUser ? log.approvalUser : "",
            style: { minWidth: "150px", maxWidth: "150px" },
        },
        {
            header: t(['openBankingLogs.company']),
            field: 'company',
            default:true,
            filter:true,
            sortable: true,
            filterConfig:{
                like:false
            },
            body: log => log.company ? log.company : "",
            style: { minWidth: "150px", maxWidth: "150px" },
        }
    ];

    const [selectedColumns, setSelectedColumns] = usePersistedState(
        columns.map((col) => ({ field: col.field, show: !!col.default, ...(col?.persisted ? { persisted: true } : {}) })),
        "openBankingLogsColumns2"
    );

    const expandableColumns = [
        {
            header: t(['openBankingLogs.action']),
            field: 'action'
        },
        {
            header: t(['openBankingLogs.message']),
            field: 'message'
        },
        {
            header: t(['openBankingLogs.source']),
            field: 'source'
        }
    ]

    const rowExpansionTemplate = (e) => {
        return (
            <div className='prime-header-sticky' style={{ marginBottom: 20 }}>
                <PrimeTable
                    value={expandedRows}
                    columns={expandableColumns}
                    simpleTable={true}
                    showFilters={false}
                    paginator={false}
                    filterDisplay={false}
                    responsiveLayout="scroll"
                />
            </div>
        )
    }

    const headerIcons = (
        <div className='d-flex align-items-center gap-2'>
            <DateRange
                size="sm"
                fromDate={filters.fromDate}
                toDate={filters.toDate}
                onChange={(dates) => {
                    const [fromDate, toDate] = dates;
                    if (fromDate && toDate) {
                        getFilteredResults({ fromDate, toDate });
                    }
                    setFilters((filters) => ({
                        ...filters,
                        fromDate,
                        toDate,
                    }));
                }}
            />
            <ExportCsvPrime
                selectedColumns={selectedColumns}
                iconOnly
                tooltip="Export CSV"
                data={logsForCSV}
                columns={columns}
                onClick={() => getFilteredResults({ getLogsForCsv: true, fromDate: filters.fromDate, toDate: filters.toDate })}
                excludedColumns={["expand"]}
            />
        </div>
    )

    return (
        state.logs ?
            <>
                <PrimeTable
                        loading={loading}
                        columns={columns}
                        value={[...state.logs]}
                        rowExpansionTemplate={rowExpansionTemplate}
                        globalFilterValue={[searchQuery, setSearchQuery]}
                        tableRows={[tableRows, setTableRows]}
                        clearFilters={true}
                        showSelectColumns
                        gridKey="openBankingLogsColumns2"
                        selectedColumns={[selectedColumns, setSelectedColumns]}
                        reorderableColumns={true}
                        onRowToggle={(e) => setExpandedRows(e.data)}
                        persistedRowPerPage={[persistedRowPerPage, setPersistedRowPerPage]}
                        filters={[filters, setFilters]}
                        defaultFilters={defaultFilters}
                        expandedRows={expandedRows}
                        defaultSortCriteria={defaultSortCriteria}
                        headerButtonsLeft={headerButtonsLeft}
                        headerIcons={headerIcons}
                        onRemoteDataChange={(remoteData) => {
                            const updatedFilters = remoteData.filters ?? filters;
                            const updatedTableData = remoteData.tableRows ?? tableRows;
                            let { currentPage, rowsPerPage, sortCriteria } = updatedTableData;
                            sortCriteria = sortCriteria ? [sortCriteria] : [];
                            getFilteredResults({
                                updatedFilters,
                                pageSize: rowsPerPage,
                                page: currentPage,
                                sortCriteria,
                                fromDate: updatedFilters.fromDate,
                                toDate: updatedFilters.toDate
                            });
                        }}
                        columnDeps={[companyFilterParams.companyId]}
                    />
            </>
            :
            <Loader />
    )
}

export default OpenBankingLogs
